import { useEffect, useMemo, useState } from "react";
import "../start.css";
import {
  categories,
  experiences,
  goals,
  interests,
  programmes,
  rankLabels,
  testimonials,
} from "../data/finderData";
import bankoleImage from "../../../assets/images/png/bankole-adesina-v1.png";
import chideraImage from "../../../assets/images/png/chidera-robert-v1.png";
import africanTechTeam from "../../../assets/images/png/african-tech-team.webp";
import { Link } from "react-router-dom";
import { getCohortsApi } from "../../../store/actions";

const testimonialImages = {
  "Bankolé Adesina": bankoleImage,
  "Chidera Robert": chideraImage,
};

function findLabel(list, id) {
  return list.find((item) => item.id === id)?.label ?? "";
}

function scoreProgramme(programme, answers) {
  let score = 0;
  if (answers.interest && programme.interests.includes(answers.interest)) {
    score += 50;
  }
  if (
    answers.experience &&
    programme.experiences.includes(answers.experience)
  ) {
    score += 20;
  }
  if (answers.goal && programme.goals.includes(answers.goal)) {
    score += 30;
  }
  return score;
}

function matchPercent(score, rank) {
  const base = score >= 100 ? 96 : score >= 80 ? 90 : score >= 50 ? 82 : 74;
  return Math.max(70, base - rank * 2);
}

function parseCohortDate(value) {
  if (!/\d{4}/.test(value)) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : new Date(parsed);
}

// Programmes with a course page share the cohort date set in the admin, so
// they show the same date as that page. The MBA keeps its own intake.
function cohortInfo(programme, cohortDate) {
  const usesSharedCohort =
    cohortDate && programme.coursePageUrl?.startsWith("/courses/");
  const nextCohort = usesSharedCohort ? cohortDate : programme.nextCohort;
  const status = usesSharedCohort ? "Cohort starting soon" : programme.status;
  const date = parseCohortDate(nextCohort);
  if (!date) {
    return {
      label: nextCohort,
      status,
      upcoming: false,
    };
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  if (date < today) {
    return {
      label: "Check the next intake",
      status: "Next intake to be confirmed",
      upcoming: false,
    };
  }
  return {
    label: nextCohort,
    status,
    upcoming: true,
  };
}

const ProgrammeActions = ({ programme, onCommunity }) => (
  <div className="uv-programme-actions">
    <a className="uv-enrol-button" href={programme.enrolUrl}>
      Enrol now <span aria-hidden="true">↗</span>
    </a>
    <button type="button" onClick={() => onCommunity(programme)}>
      Join community
    </button>
    <Link className="uv-learn-link" to={programme.coursePageUrl}>
      Learn more <span aria-hidden="true">→</span>
    </Link>
  </div>
);

const CommunityModal = ({ programme, onClose }) => {
  const message = encodeURIComponent(
    `Hi Utiva, I would like to join the ${programme.communityName}.`
  );
  return (
    <div
      className="uv-modal-layer"
      role="presentation"
      onMouseDown={(e) => {
        if (e.currentTarget === e.target) onClose();
      }}
    >
      <section
        className="uv-community-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="community-title"
      >
        <button
          type="button"
          aria-label="Close community dialog"
          onClick={onClose}
        >
          ×
        </button>
        <span className="uv-modal-symbol">UT</span>
        <p>JOIN THE COMMUNITY</p>
        <h2 id="community-title">Join the {programme.communityName}</h2>
        <p>
          Connect with learners, alumni and people building careers in{" "}
          {programme.category}.
        </p>
        <ul>
          <li>Learning opportunities</li>
          <li>Community events</li>
          <li>Career conversations</li>
          <li>Programme updates</li>
        </ul>
        <a href={`https://wa.me/2348035152982?text=${message}`}>
          Continue to Utiva on WhatsApp <span aria-hidden="true">↗</span>
        </a>
        <small>
          Utiva will connect you to the appropriate official community.
        </small>
      </section>
    </div>
  );
};

const ProgrammeCard = ({
  programme,
  compareSelected,
  onCompare,
  onCommunity,
  onActivate,
  cohortDate,
}) => {
  const cohort = cohortInfo(programme, cohortDate);
  return (
    <article
      className="uv-programme-card"
      onMouseEnter={() => onActivate(programme)}
    >
      <div className="uv-programme-visual" data-category={programme.category}>
        <span>{programme.category}</span>
        <strong>{programme.code}</strong>
      </div>
      <div className="uv-programme-body">
        <div className="uv-programme-heading">
          <div>
            <p>{programme.level}</p>
            <h3 className="font-bold">{programme.name}</h3>
          </div>
          <button
            className={compareSelected ? "uv-compare-active" : ""}
            type="button"
            onClick={() => onCompare(programme)}
          >
            {compareSelected ? "✓ Comparing" : "+ Compare"}
          </button>
        </div>
        <p className="uv-programme-outcome">{programme.outcome}</p>
        <div
          className={`uv-cohort-status ${
            cohort.upcoming ? "uv-cohort-soon" : ""
          }`}
        >
          <span>{cohort.status}</span>
          <strong>{cohort.label}</strong>
        </div>
        <dl className="uv-programme-facts">
          <div>
            <dt>Duration</dt>
            <dd>{programme.duration}</dd>
          </div>
          <div>
            <dt>Format</dt>
            <dd>{programme.format}</dd>
          </div>
          <div>
            <dt>Schedule</dt>
            <dd>{programme.schedule}</dd>
          </div>
        </dl>
        <div className="uv-career-row">
          <span>Possible careers</span>
          <p>{programme.careerOutcomes.join(" · ")}</p>
        </div>
        <ProgrammeActions programme={programme} onCommunity={onCommunity} />
      </div>
    </article>
  );
};

const RecommendationCard = ({
  programme,
  rank,
  score,
  answers,
  onCommunity,
  onCompare,
  compareSelected,
  cohortDate,
}) => {
  const cohort = cohortInfo(programme, cohortDate);
  return (
    <article
      className={`uv-recommendation-card ${
        rank === "BEST MATCH" ? "uv-best-match" : ""
      }`}
    >
      <div className="uv-rank-row">
        <span>{rank}</span>
        <strong>
          {score}% <small>MATCH</small>
        </strong>
      </div>
      <div className="uv-rec-title">
        <span>{programme.code}</span>
        <div>
          <p>{programme.category}</p>
          <h3>{programme.name}</h3>
        </div>
      </div>
      <p className="uv-rec-outcome">{programme.outcome}</p>
      <div className="uv-why-box">
        <strong>Why this fits you</strong>
        <p>
          You chose <b>{findLabel(interests, answers.interest)}</b>, said{" "}
          <b>{findLabel(experiences, answers.experience).toLowerCase()}</b>, and
          want to <b>{findLabel(goals, answers.goal).toLowerCase()}</b>.
        </p>
      </div>
      <div
        className={`uv-cohort-status ${
          cohort.upcoming ? "uv-cohort-soon" : ""
        }`}
      >
        <span>{cohort.status}</span>
        <strong>{cohort.label}</strong>
      </div>
      <dl className="uv-rec-facts">
        <div>
          <dt>Duration</dt>
          <dd>{programme.duration}</dd>
        </div>
        <div>
          <dt>Format</dt>
          <dd>{programme.format}</dd>
        </div>
        <div>
          <dt>Level</dt>
          <dd>{programme.level}</dd>
        </div>
      </dl>
      <div className="uv-rec-careers">
        <span>Career possibilities</span>
        <p>{programme.careerOutcomes.join(" · ")}</p>
      </div>
      <ProgrammeActions programme={programme} onCommunity={onCommunity} />
      <button
        className={`uv-rec-compare ${
          compareSelected ? "uv-compare-active" : ""
        }`}
        type="button"
        onClick={() => onCompare(programme)}
      >
        {compareSelected ? "✓ Added to comparison" : "+ Add to comparison"}
      </button>
    </article>
  );
};

const StartFinderExperience = () => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({});
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [communityProgramme, setCommunityProgramme] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [stickyProgramme, setStickyProgramme] = useState(programmes[0]);
  const [cohortDate, setCohortDate] = useState("");

  useEffect(() => {
    const fetchCohortDateData = async () => {
      try {
        const params = { page: 1, limit: 1 };
        const response = await getCohortsApi(params);
        setCohortDate(response?.data?.[0]?.date || "");
      } catch (error) {
        setCohortDate("");
      }
    };
    fetchCohortDateData();
  }, []);

  const recommendations = useMemo(
    () =>
      programmes
        .map((programme) => ({
          programme,
          score: scoreProgramme(programme, answers),
        }))
        .sort(
          (a, b) =>
            b.score - a.score ||
            a.programme.name.localeCompare(b.programme.name)
        )
        .slice(0, 3),
    [answers]
  );

  const filteredProgrammes = useMemo(
    () =>
      programmes.filter((programme) => {
        const inCategory =
          category === "All" || programme.category === category;
        const query = search.trim().toLowerCase();
        const matchesQuery =
          !query ||
          `${programme.name} ${programme.category} ${
            programme.outcome
          } ${programme.careerOutcomes.join(" ")}`
            .toLowerCase()
            .includes(query);
        return inCategory && matchesQuery;
      }),
    [category, search]
  );

  const compareProgrammes = compareIds
    .map((id) => programmes.find((p) => p.id === id))
    .filter(Boolean);

  const chooseInterest = (id) => {
    setAnswers((prev) => ({ ...prev, interest: id }));
    setStep(2);
  };
  const chooseExperience = (id) => {
    setAnswers((prev) => ({ ...prev, experience: id }));
    setStep(3);
  };
  const chooseGoal = (id) => {
    setAnswers((prev) => ({ ...prev, goal: id }));
    setStep(4);
    setTimeout(
      () =>
        document
          .getElementById("recommendations")
          ?.scrollIntoView({ behavior: "smooth", block: "start" }),
      80
    );
  };
  const retakeFinder = () => {
    setAnswers({});
    setStep(1);
    document
      .getElementById("finder")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const toggleCompare = (programme) => {
    setCompareIds((prev) =>
      prev.includes(programme.id)
        ? prev.filter((id) => id !== programme.id)
        : prev.length < 3
        ? [...prev, programme.id]
        : prev
    );
  };

  return (
    <main className="uv-start" id="page-top">
      <section className="uv-hero">
        <div className="uv-hero-copy">
          <p className="uv-eyebrow">FIND YOUR UTIVA PROGRAMME</p>
          <h1>
            Find the right course. <em>Start building.</em>
          </h1>
          <p>
            Choose what interests you and get your best programme matches in
            three quick steps.
          </p>
          <div className="uv-hero-actions">
            <a href="#finder">
              Help me choose <span aria-hidden="true">↓</span>
            </a>
            <a href="#programmes">
              View all programmes <span aria-hidden="true">→</span>
            </a>
          </div>
          <div className="uv-hero-meta" aria-label="Course finder details">
            <span>3 quick questions</span>
            <span>Under 30 seconds</span>
            <span>No signup</span>
          </div>
        </div>
      </section>

      <section className="uv-finder" id="finder" aria-labelledby="finder-title">
        <div className="uv-progress-header">
          <div>
            <span>{step === 4 ? "YOUR RESULTS" : `STEP ${step} OF 3`}</span>
            <h2 id="finder-title">
              {step === 1
                ? "Which future sounds most like you?"
                : step === 2
                ? "Where are you starting from?"
                : step === 3
                ? "What do you want this skill to help you do?"
                : "Your course matches are ready."}
            </h2>
            <p>
              {step === 1
                ? "Choose one career world. Your strongest Utiva matches are only two more clicks away."
                : step === 2
                ? "Choose the option that best describes your experience today."
                : step === 3
                ? "Tell us your goal so we can rank the strongest programmes for you."
                : "We used your three answers to find the strongest places to start."}
            </p>
          </div>
          {step < 4 && <b>{step} / 3</b>}
        </div>

        <div className="uv-progress-track">
          <span style={{ width: `${(Math.min(step, 3) / 3) * 100}%` }} />
        </div>

        {step === 1 && (
          <div className="uv-interest-grid">
            {interests.map((interest, index) => (
              <button
                key={interest.id}
                type="button"
                data-interest={interest.id}
                onClick={() => chooseInterest(interest.id)}
              >
                <span className="uv-interest-icon">{interest.symbol}</span>
                <span className="uv-interest-index">0{index + 1}</span>
                <strong>{interest.label}</strong>
                <small>{interest.description}</small>
                <b aria-hidden="true">
                  Choose this path <span>→</span>
                </b>
              </button>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="uv-experience-grid">
            {experiences.map((experience, index) => (
              <button
                key={experience.id}
                type="button"
                onClick={() => chooseExperience(experience.id)}
              >
                <span>0{index + 1}</span>
                <strong>{experience.label}</strong>
                <small>{experience.description}</small>
                <b aria-hidden="true">→</b>
              </button>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="uv-goal-grid">
            {goals.map((goal, index) => (
              <button
                key={goal.id}
                type="button"
                onClick={() => chooseGoal(goal.id)}
              >
                <span>{String(index + 1).padStart(2, "0")}</span>
                <strong>{goal.label}</strong>
                <b aria-hidden="true">→</b>
              </button>
            ))}
          </div>
        )}

        {step > 1 && step < 4 && (
          <button
            className="uv-back-button"
            type="button"
            onClick={() => setStep(step - 1)}
          >
            ← Back
          </button>
        )}

        {step === 4 && (
          <button
            className="uv-view-results-button"
            type="button"
            onClick={() =>
              document
                .getElementById("recommendations")
                ?.scrollIntoView({ behavior: "smooth" })
            }
          >
            See my matches <span aria-hidden="true">↓</span>
          </button>
        )}
      </section>

      {step === 4 && (
        <section
          className="uv-recommendations"
          id="recommendations"
          aria-labelledby="recommendations-title"
        >
          <div className="uv-section-heading">
            <div>
              <p className="uv-eyebrow">PERSONALISED FOR YOU</p>
              <h2 id="recommendations-title">We found your best matches.</h2>
            </div>
            <div>
              <p>
                Based on what you told us, these programmes are the strongest
                fit for where you want to go.
              </p>
              <button type="button" onClick={retakeFinder}>
                Not quite right? Retake the finder
              </button>
            </div>
          </div>
          <div className="uv-recommendation-grid">
            {recommendations.map(({ programme, score }, index) => (
              <RecommendationCard
                key={programme.id}
                programme={programme}
                rank={rankLabels[index]}
                score={matchPercent(score, index)}
                answers={answers}
                onCommunity={setCommunityProgramme}
                onCompare={toggleCompare}
                compareSelected={compareIds.includes(programme.id)}
                cohortDate={cohortDate}
              />
            ))}
          </div>
        </section>
      )}

      <section
        className="uv-catalogue"
        id="programmes"
        aria-labelledby="programmes-title"
      >
        <div className="uv-section-heading">
          <div>
            <p className="uv-eyebrow">ALL UTIVA PROGRAMMES</p>
            <h2 id="programmes-title">Or explore every pathway.</h2>
          </div>
          <p>
            Filter instantly by school or search by programme, skill or career
            outcome. Each card gives you enough context to take the next step.
          </p>
        </div>

        <div className="uv-catalogue-controls">
          <label>
            <span>Search programmes</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Try Data Analytics or Cybersecurity"
            />
          </label>
          <div
            className="uv-filters"
            role="group"
            aria-label="Filter programmes by category"
          >
            {categories.map((item) => (
              <button
                key={item}
                className={category === item ? "uv-filter-active" : ""}
                type="button"
                onClick={() => setCategory(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        <p className="uv-result-count">
          {filteredProgrammes.length}{" "}
          {filteredProgrammes.length === 1 ? "programme" : "programmes"} shown
        </p>

        <div className="uv-programme-grid">
          {filteredProgrammes.map((programme) => (
            <ProgrammeCard
              key={programme.id}
              programme={programme}
              compareSelected={compareIds.includes(programme.id)}
              onCompare={toggleCompare}
              onCommunity={setCommunityProgramme}
              onActivate={setStickyProgramme}
              cohortDate={cohortDate}
            />
          ))}
        </div>

        {filteredProgrammes.length === 0 && (
          <div className="uv-empty-state">
            <strong>No exact match yet.</strong>
            <p>Try another category or a broader search term.</p>
            <button
              type="button"
              onClick={() => {
                setCategory("All");
                setSearch("");
              }}
            >
              Show all programmes
            </button>
          </div>
        )}
      </section>

      <section className="uv-why-section" aria-labelledby="why-title">
        <div>
          <p className="uv-eyebrow">WHY LEARN WITH UTIVA</p>
          <h2 id="why-title">Learn it. Build it. Prove it.</h2>
          <p>
            Practical learning is designed to help you move from understanding a
            skill to showing what you can do with it.
          </p>
        </div>
        <div className="uv-why-grid">
          <article>
            <span>01</span>
            <h3>Live expert guidance</h3>
            <p>
              Learn with practitioners who can explain the decisions behind the
              work.
            </p>
          </article>
          <article>
            <span>02</span>
            <h3>Projects with purpose</h3>
            <p>
              Apply your learning to realistic problems and build evidence of
              capability.
            </p>
          </article>
          <article>
            <span>03</span>
            <h3>Community that moves</h3>
            <p>
              Learn alongside ambitious people and stay connected beyond the
              classroom.
            </p>
          </article>
          <article>
            <span>04</span>
            <h3>Career-ready proof</h3>
            <p>
              Build a portfolio and credential that make progress easier to
              communicate.
            </p>
          </article>
        </div>
      </section>

      <section className="uv-story-section" aria-labelledby="stories-title">
        <div className="uv-section-heading">
          <div>
            <p className="uv-eyebrow">REAL LEARNER MOMENTUM</p>
            <h2 id="stories-title">People building possible futures.</h2>
          </div>
          <p>
            Learning becomes more meaningful when you can see how other people
            used it to build confidence, projects and career direction.
          </p>
        </div>
        <div className="uv-story-grid">
          {testimonials.map((testimonial) => (
            <blockquote key={testimonial.name}>
              <span>&#8220;</span>
              <p>{testimonial.quote}</p>
              <footer>
                {testimonialImages[testimonial.name] ? (
                  <img
                    src={testimonialImages[testimonial.name]}
                    alt={testimonial.name}
                  />
                ) : (
                  <b>{testimonial.initials}</b>
                )}
                <div>
                  <strong>{testimonial.name}</strong>
                  <small>{testimonial.role}</small>
                </div>
              </footer>
            </blockquote>
          ))}
        </div>
      </section>

      <section
        className="uv-community-section"
        aria-labelledby="community-heading"
      >
        <div className="uv-community-image">
          <img
            src={africanTechTeam}
            alt="African technology professionals collaborating"
          />
          <div>
            <span>200K+</span>
            <p>People across the Utiva ecosystem</p>
          </div>
        </div>
        <div>
          <p className="uv-eyebrow">NOT READY TO ENROL?</p>
          <h2 id="community-heading">
            Join the room where your next career is being built.
          </h2>
          <p>
            Community gives you a lower-pressure way to learn about the field,
            meet people on the journey and hear about programmes when you are
            ready.
          </p>
          <button
            type="button"
            onClick={() => setCommunityProgramme(stickyProgramme)}
          >
            Choose a community <span aria-hidden="true">→</span>
          </button>
        </div>
      </section>

      <section className="uv-final-cta">
        <div>
          <p className="uv-eyebrow">YOUR NEXT STEP</p>
          <h2>Still unsure? Three clicks can make it clearer.</h2>
          <p>
            No signup. No lead form. Just a useful recommendation based on what
            excites you and where you want to go.
          </p>
        </div>
        <a href="#finder">
          Find my course <span aria-hidden="true">↑</span>
        </a>
      </section>

      {communityProgramme && (
        <CommunityModal
          programme={communityProgramme}
          onClose={() => setCommunityProgramme(null)}
        />
      )}

      {compareIds.length > 0 && (
        <aside className="uv-compare-bar">
          <div>
            <strong>{compareIds.length}/3 selected</strong>
            <span>{compareProgrammes.map((p) => p.code).join(" · ")}</span>
          </div>
          <button
            type="button"
            onClick={() => setCompareOpen(true)}
            disabled={compareIds.length < 2}
          >
            Compare programmes
          </button>
          <button
            type="button"
            aria-label="Clear comparison"
            onClick={() => setCompareIds([])}
          >
            ×
          </button>
        </aside>
      )}

      {compareOpen && (
        <div
          className="uv-modal-layer"
          role="presentation"
          onMouseDown={(e) => {
            if (e.currentTarget === e.target) setCompareOpen(false);
          }}
        >
          <section
            className="uv-compare-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="compare-title"
          >
            <header>
              <div>
                <p>PROGRAMME COMPARISON</p>
                <h2 id="compare-title">See the difference clearly.</h2>
              </div>
              <button
                type="button"
                aria-label="Close comparison"
                onClick={() => setCompareOpen(false)}
              >
                ×
              </button>
            </header>
            <div className="uv-compare-table">
              <div className="uv-compare-labels">
                <strong>Programme</strong>
                <span>Difficulty</span>
                <span>Beginner friendly</span>
                <span>Duration</span>
                <span>Tools</span>
                <span>Career paths</span>
                <span>Next cohort</span>
                <span>Price</span>
              </div>
              {compareProgrammes.map((programme) => {
                const cohort = cohortInfo(programme, cohortDate);
                return (
                  <div key={programme.id}>
                    <strong>{programme.name}</strong>
                    <span>{programme.difficulty}</span>
                    <span>{programme.level}</span>
                    <span>{programme.duration}</span>
                    <span>{programme.tools.join(" · ")}</span>
                    <span>{programme.careerOutcomes.join(" · ")}</span>
                    <span>{cohort.label}</span>
                    <span>{programme.price}</span>
                    <a href={programme.enrolUrl}>Enrol now</a>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      )}

      <aside className="uv-mobile-sticky">
        <div>
          <strong>{stickyProgramme.name}</strong>
          <span>{cohortInfo(stickyProgramme, cohortDate).label}</span>
        </div>
        <a href={stickyProgramme.enrolUrl}>Enrol now</a>
      </aside>
    </main>
  );
};

export default StartFinderExperience;
